
import React, { useEffect, useRef } from 'react';
// Note: JsBarcode is loaded via CDN in index.html

declare var JsBarcode: any;

interface PrintableCustomLabelProps {
  widthInches: number;
  heightInches: number;
  title: string;
  bodyLines: string[];
  footer: string;
  barcodeData: string; 
  showBarcode: boolean;  
  fontSizePt: number;
}

const PREVIEW_DPI = 96; // Pixels per inch for screen preview

export const PrintableCustomLabel: React.FC<PrintableCustomLabelProps> = ({
  widthInches,
  heightInches,
  title,
  bodyLines,
  footer,
  barcodeData,
  showBarcode,
  fontSizePt,
}) => {
  const barcodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (showBarcode && barcodeData && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, barcodeData, {
          format: "CODE128",
          width: 1.5, 
          height: 30, // Slightly reduced height for custom labels
          displayValue: true,
          fontSize: Math.max(8, fontSizePt - 2), // Smaller font for barcode text
          textMargin: 0,
          margin: 2, // Smaller margin
        });
      } catch (e) {
        console.error("Barcode generation failed for custom label:", e);
        if (barcodeRef.current) {
            barcodeRef.current.innerHTML = '<text x="5" y="15" fill="red" font-size="8">Error</text>';
        }
      }
    } else if (barcodeRef.current) {
        barcodeRef.current.innerHTML = ''; // Clear barcode if not shown or no data
    }
  }, [barcodeData, showBarcode, widthInches, heightInches, fontSizePt]);

  const labelStyle: React.CSSProperties = {
    width: `${widthInches * PREVIEW_DPI}px`,
    height: `${heightInches * PREVIEW_DPI}px`,
    fontSize: `${fontSizePt}pt`,
    border: '1px dashed #9ca3af', 
    padding: '0.1in', 
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    color: 'black',
    lineHeight: '1.3', 
  };

  const barcodeContainerStyle: React.CSSProperties = {
    maxWidth: '40%', // Limit barcode width
    height: '50px', // Fixed height for barcode area
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end', // Align to the right of its container
  };

  return (
    <div style={labelStyle} className="font-sans">
      <div className="flex-grow overflow-hidden flex flex-col">
        {title && <h1 className="text-center font-bold" style={{ fontSize: `${Math.min(14, Math.max(fontSizePt, 10))}pt`, marginBottom: '0.05in', flexShrink: 0 }}>{title}</h1>}
        
        <div className={`flex-grow flex ${showBarcode && barcodeData ? 'flex-row' : 'flex-col'} items-start`}>
          <div className={` ${showBarcode && barcodeData ? 'w-3/5 pr-1' : 'w-full'} overflow-y-auto`}> {/* Text content takes more space and can scroll if needed */}
            {bodyLines.map((line, index) => (
              <p key={index} className="my-0.5 break-words" style={{ fontSize: `${fontSizePt}pt` }}>{line}</p>
            ))}
          </div>
          {showBarcode && barcodeData && (
            <div style={barcodeContainerStyle} className="pl-1 flex-shrink-0"> {/* Barcode container, prevents shrinking */}
              <svg ref={barcodeRef} className="max-w-full max-h-full"></svg>
            </div>
          )}
        </div>
      </div>

      {footer && (
        <div className="text-right text-[0.8em] border-t border-dashed border-gray-300 pt-1 mt-auto flex-shrink-0">
          <p className="break-words" style={{ fontSize: `${Math.max(6, fontSizePt - 2)}pt` }}>{footer}</p>
        </div>
      )}
    </div>
  );
};
